import React, { useState, useEffect } from 'react';
import {
  getAdminStatistics,
  getAdminFolders,
  downloadFolderZip,
  bulkDeleteFolders,
  getAdminFolderDetails
} from '../../api/api';
import AdminFolderPreview from './AdminFolderPreview';
import AdminSizeManager from './AdminSizeManager';
import AdminProductTypeManager from './AdminProductTypeManager';

const AdminDashboard = ({ onLogout }) => {
  const [statistics, setStatistics] = useState(null);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [previewFolder, setPreviewFolder] = useState(null);
  const [showSizeManager, setShowSizeManager] = useState(false);
  const [showProductTypeManager, setShowProductTypeManager] = useState(false);
  const [downloadingFolder, setDownloadingFolder] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsResponse, foldersResponse] = await Promise.all([
        getAdminStatistics(),
        getAdminFolders()
      ]);

      setStatistics(statsResponse.statistics);
      setFolders(foldersResponse.folders);
    } catch (error) {
      console.error('Error loading admin data:', error);
      alert('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = async () => {
    try {
      setLoading(true);
      const response = await getAdminFolders(startDate, endDate);
      setFolders(response.folders);
    } catch (error) {
      console.error('Error filtering folders:', error);
      alert('Failed to filter folders');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilter = async () => {
    setStartDate('');
    setEndDate('');
    try {
      setLoading(true);
      const response = await getAdminFolders();
      setFolders(response.folders);
    } catch (error) {
      console.error('Error loading folders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectFolder = (sessionId) => {
    if (selectedFolders.includes(sessionId)) {
      setSelectedFolders(selectedFolders.filter(id => id !== sessionId));
    } else {
      setSelectedFolders([...selectedFolders, sessionId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedFolders.length === folders.length) {
      setSelectedFolders([]);
    } else {
      setSelectedFolders(folders.map(f => f.sessionId));
    }
  };

  const handleDownload = async (sessionId) => {
    try {
      setDownloadingFolder(sessionId);
      const response = await downloadFolderZip(sessionId);
      
      // Get filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'download.zip';
      if (contentDisposition) {
        const matches = /filename="([^"]+)"/.exec(contentDisposition);
        if (matches && matches[1]) filename = matches[1];
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading folder:', error);
      alert('Failed to download folder');
    } finally {
      setDownloadingFolder(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedFolders.length === 0) {
      alert('Please select folders to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedFolders.length} folder(s)? This cannot be undone.`)) {
      return;
    }

    try {
      await bulkDeleteFolders(selectedFolders);
      alert('Folders deleted successfully');
      setSelectedFolders([]);
      loadData();
    } catch (error) {
      console.error('Error deleting folders:', error);
      alert('Failed to delete folders');
    }
  };

  const handleViewFolder = async (sessionId) => {
    try {
      const response = await getAdminFolderDetails(sessionId);
      setPreviewFolder(response.folder);
    } catch (error) {
      console.error('Error loading folder details:', error);
      alert('Failed to load folder details');
    }
  };

  const copySessionLink = (sessionId) => {
    const link = `${window.location.origin}?session=${sessionId}`;
    navigator.clipboard.writeText(link);
    alert('Session link copied to clipboard!');
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading && !statistics) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{
        background: 'linear-gradient(to bottom right, #FFE5F1, #E27C7A)'
      }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-700">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8" style={{
      background: 'linear-gradient(to bottom right, #FFE5F1, #E27C7A)'
    }}>
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
        <div className="flex justify-between items-center flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">📊 Admin Dashboard</h1>
            <p className="text-gray-600">Manage all folders and settings</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowProductTypeManager(!showProductTypeManager)}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors"
            >
              🏷️ Manage Product Types
            </button>
            <button
              onClick={() => setShowSizeManager(!showSizeManager)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
            >
              📏 Manage Sizes
            </button>
            <button
              onClick={onLogout}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Product Type Manager Modal */}
      {showProductTypeManager && (
        <AdminProductTypeManager onClose={() => setShowProductTypeManager(false)} />
      )}

      {/* Size Manager Modal */}
      {showSizeManager && (
        <AdminSizeManager onClose={() => setShowSizeManager(false)} />
      )}

      {/* Folder Preview Modal */}
      {previewFolder && (
        <AdminFolderPreview
          folder={previewFolder}
          onClose={() => setPreviewFolder(null)}
          onDownload={handleDownload}
        />
      )}

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Folders</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">{statistics.totalFolders}</p>
              </div>
              <div className="text-4xl">📁</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Photos</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">{statistics.totalPhotos}</p>
              </div>
              <div className="text-4xl">📸</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Size</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">{formatBytes(statistics.totalSize)}</p>
              </div>
              <div className="text-4xl">💾</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Latest Upload</p>
                <p className="text-sm font-semibold text-gray-800 mt-2">
                  {statistics.latestUpload ? new Date(statistics.latestUpload).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div className="text-4xl">⏰</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">🔍 Filter Folders</h2>
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <button
            onClick={handleFilter}
            className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-colors"
          >
            Apply Filter
          </button>
          <button
            onClick={handleClearFilter}
            className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedFolders.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-4 mb-6 flex items-center justify-between">
          <p className="text-gray-800 font-semibold">{selectedFolders.length} folder(s) selected</p>
          <button
            onClick={handleBulkDelete}
            className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
          >
            🗑️ Delete Selected
          </button>
        </div>
      )}

      {/* Folders List */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">📂 All Folders ({folders.length})</h2>
          <button
            onClick={handleSelectAll}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-semibold transition-colors"
          >
            {selectedFolders.length === folders.length ? 'Deselect All' : 'Select All'}
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : folders.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No folders found
          </div>
        ) : (
          <div className="space-y-4">
            {folders.map((folder) => (
              <div
                key={folder.sessionId}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-4">
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={selectedFolders.includes(folder.sessionId)}
                    onChange={() => handleSelectFolder(folder.sessionId)}
                    className="mt-1 w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                  />

                  {/* Folder Info */}
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="text-lg font-bold text-gray-800">{folder.name}</h3>
                        <p className="text-sm text-gray-500">{formatDate(folder.createdAt)}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                      <div>
                        <span className="text-gray-600">Print Size:</span>
                        <span className="ml-2 font-semibold text-gray-800">{folder.printSize}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Quantity:</span>
                        <span className="ml-2 font-semibold text-gray-800">{folder.quantity}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Photos:</span>
                        <span className="ml-2 font-semibold text-gray-800">{folder.totalPhotos}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Size:</span>
                        <span className="ml-2 font-semibold text-gray-800">{formatBytes(folder.totalSize)}</span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => handleViewFolder(folder.sessionId)}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors"
                      >
                        👁️ View
                      </button>
                      <button
                        onClick={() => handleDownload(folder.sessionId)}
                        disabled={downloadingFolder === folder.sessionId}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {downloadingFolder === folder.sessionId ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                            Downloading...
                          </>
                        ) : (
                          <>📥 Download ZIP</>
                        )}
                      </button>
                      <button
                        onClick={() => copySessionLink(folder.sessionId)}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition-colors"
                      >
                        📋 Copy Link
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;

